import React, { Suspense } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { useFirebase } from 'react-redux-firebase';
import { connect } from 'react-redux';
import { IntlProvider } from 'react-intl';
// import { StyledCenteredWrapper } from "./layout/elements";
import useFirestoreListener from './hooks/useFirestoreListener';
import {
  DICTIONARY_COLLECTION,
  TESTS_COLLECTION,
  WORDS_COLLECTION,
  BEGINNER_TESTS_COLLECTION,
} from './constants';
import AlertHandler from './components/UI/Alert/AlertHandler';
// Language files
import gb from './locale/gb.json';
import rs from './locale/rs.json';
import es from './locale/es.json';
import de from './locale/de.json';

import Layout from './layout/Layout';
import Loader from './components/UI/Loader/Loader';
import useLanguage from './hooks/useLanguage';
import ErrorBoundary from './pages/ErrorBoundary/ErrorBoundary';
// Home routes
const Home = React.lazy(() => import('./pages/Home'));
const HowToUse = React.lazy(() => import('./pages/HowToUse/HowToUse'));
const Vision = React.lazy(() => import('./pages/Vision/Vision'));
const Privacy = React.lazy(() => import('./pages/Privacy/Privacy'));
const TermsOfUse = React.lazy(() => import('./pages/TermsOfUse/TermsOfUse'));
// Auth routes
const Login = React.lazy(() => import('./pages/Auth/Login/Login'));
const SignUp = React.lazy(() => import('./pages/Auth/SignUp/SignUp'));
const Logout = React.lazy(() => import('./pages/Auth/Logout/Logout'));
const VerifyEmail = React.lazy(() =>
  import('./pages/Auth/VerifyEmail/VerifyEmail')
);
// App routes
const Dashboard = React.lazy(() => import('./pages/app/Dashboard/Dashboard'));
const Dictionary = React.lazy(() =>
  import('./pages/app/Dictionary/Dictionary')
);
const TestVerbum = React.lazy(() =>
  import('./pages/app/TestVerbum/TestVerbum')
);
const Test = React.lazy(() => import('./pages/app/TestVerbum/Test'));
const Practice = React.lazy(() => import('./pages/app/Practice/Practice'));
const Vocabulary = React.lazy(() =>
  import('./pages/app/Vocabulary/Vocabulary')
);
const Profile = React.lazy(() => import('./pages/app/Profile/Profile'));
const PracticeTest = React.lazy(() =>
  import('./pages/app/Practice/PracticeTest')
);
// Buy routes
const BuyPremium = React.lazy(() => import('./pages/Buy/BuyPremium'));
// Admin routes
const AdminPanel = React.lazy(() => import('./pages/admin/AdminPanel'));
const CreateStoreTest = React.lazy(() =>
  import('./pages/admin/CreateStoreTest')
);
const EditStoreTest = React.lazy(() => import('./pages/admin/EditStoreTest'));

// const getMessagesContent = async (lng) => {
//   const module = await import(`./locale/${lng}.json`);
//   return module.default;
// };
const LoaderWrapper = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const languageFiles = {
  gb,
  rs,
  es,
  de,
};

const App = ({
  userId,
  isLoaded,
  isAdmin,
  premiumUser,
  appAlerts,
  userProfile,
  activeDictionary,
}) => {
  // const [languageMessageCfg, setLanguageMessageCfg] = useState(null);
  const firebase = useFirebase();
  const [language] = useLanguage();
  // Lisnerest for changes in Firestore
  useFirestoreListener(
    {
      collection: DICTIONARY_COLLECTION,
      where: ['ownerId', '==', userId],
    },
    [userId]
  );
  useFirestoreListener(
    {
      collection: TESTS_COLLECTION,
      where: [
        ['dictionaryId', '==', activeDictionary?.id],
        ['isTestDone', '==', true],
      ],
    },
    [activeDictionary?.id]
  );
  useFirestoreListener(
    {
      collection: WORDS_COLLECTION,
      where: [
        ['ownerId', '==', userId],
        ['dictionaryId', '==', activeDictionary?.id],
      ],
    },
    [userId, activeDictionary?.id]
  );
  useFirestoreListener(
    {
      collection: BEGINNER_TESTS_COLLECTION,
      where: [
        ['dictionaryId', '==', activeDictionary?.id],
        ['attemptsCurrentRun', '>', 0],
      ],
    },
    [activeDictionary?.id]
  );

  let routes = null;
  if (userId) {
    // When user is logged in and
    routes = (
      <Suspense
        fallback={
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        }
      >
        <Switch>
          <Route exact path="/app/dictionary" component={Dictionary} />
          <Route exact path="/app/dashboard" component={Dashboard} />
          <Route exact path="/app/testVerbum" component={TestVerbum} />
          <Route exact path="/app/testVerbum/test/:testData" component={Test} />
          <Route exact path="/app/practice" component={Practice} />
          <Route
            exact
            path="/app/practice/:testMode/:testDifficulty"
            component={PracticeTest}
          />
          <Route exact path="/app/vocabulary" component={Vocabulary} />
          <Route exact path="/app/profile" component={Profile} />
          <Route exact path="/logout" component={Logout} />
          <Route
            exact
            path="/app/store/premium"
            render={({ location, ...props }) =>
              !premiumUser ? (
                <BuyPremium {...props} />
              ) : (
                <Redirect
                  to={{
                    pathname: '/app/dictionary',
                    state: { from: location },
                  }}
                />
              )
            }
          />
          <Route
            exact
            path={`${process.env.REACT_APP_ADMIN_PANEL_URL}`}
            render={({ location, ...props }) =>
              isAdmin ? (
                <AdminPanel {...props} />
              ) : (
                <Redirect
                  to={{
                    pathname: '/app/dictionary',
                    state: { from: location },
                  }}
                />
              )
            }
          />
          <Route
            exact
            path={`${process.env.REACT_APP_ADMIN_PANEL_URL_CREATE}`}
            render={({ location, ...props }) =>
              isAdmin ? (
                <CreateStoreTest {...props} />
              ) : (
                <Redirect
                  to={{
                    pathname: '/app/dictionary',
                    state: { from: location },
                  }}
                />
              )
            }
          />
          <Route
            exact
            path={`${process.env.REACT_APP_ADMIN_PANEL_URL_UPDATE}/:testPack/:subCollectionId/:testId`}
            render={({ location, ...props }) =>
              isAdmin ? (
                <EditStoreTest {...props} />
              ) : (
                <Redirect
                  to={{
                    pathname: '/app/dictionary',
                    state: { from: location },
                  }}
                />
              )
            }
          />
          <Redirect to="/app/dictionary" />
        </Switch>
      </Suspense>
    );
  } else {
    routes = (
      <Suspense
        fallback={
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        }
      >
        <Switch>
          <Route exact path="/" component={Home} />
          <Route exact path="/login" component={Login} />
          <Route exact path="/signup" component={SignUp} />
          <Route exact path="/verify-email" component={VerifyEmail} />
          <Route exact path="/how-to-use" component={HowToUse} />
          <Route exact path="/vision" component={Vision} />
          <Route exact path="/privacy" component={Privacy} />
          <Route exact path="/terms-of-use" component={TermsOfUse} />
          <Redirect to="/" />
        </Switch>
      </Suspense>
    );
  }

  return (
    <IntlProvider locale="en" key={language} messages={languageFiles[language]}>
      <ErrorBoundary firebase={firebase}>
        {!isLoaded ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <Layout loggedIn={userId} isLoaded={isLoaded}>
            <>
              {/* Application alerts */}
              <AlertHandler alerts={appAlerts} />
              {routes}
            </>
          </Layout>
        )}
      </ErrorBoundary>
    </IntlProvider>
  );
};

const mapStateToProps = ({ firebase, firestore, store, auth }) => ({
  premiumUser: firebase.profile.premium,
  isLoaded: firebase.profile.isLoaded,
  appAlerts: [...auth.authAlerts, ...store.storeAlerts],
  userProfile: firebase.profile,
  userId: firebase.auth.uid,
  activeDictionary: firestore?.ordered?.dictionary?.find(
    (diction) => diction.isActive
  ),
  isAdmin:
    firebase?.profile?.admin &&
    firebase.profile.admin === process.env.REACT_APP_ADMIN_KEY,
});

export default connect(mapStateToProps, null)(App);
